import {
  addMonths,
  endOfDay,
  format,
  isSameDay,
  isSameMonth,
  isToday,
  startOfDay,
  subMonths,
} from 'date-fns';
import { Dispatch, FunctionComponent, SetStateAction, memo, useCallback } from 'react';
import sprite from '../../../../assets/icons/sprite.svg';
import { useMonthGrid } from '../../../hooks';
import { getDateFnsLocale } from '../../../utils';

import classNames from 'classnames';
import classes from './CalendarGrid.module.scss';

interface CalendarGridProps {
  viewDate: Date;
  setViewDate: Dispatch<SetStateAction<Date>>;
  selectedDay: Date | null;
  onSelectDay: (day: Date) => void;
  minDate?: Date | null;
  maxDate?: Date | null;
}

export const CalendarGrid: FunctionComponent<CalendarGridProps> = memo(
  ({ viewDate, setViewDate, selectedDay, onSelectDay, minDate, maxDate }) => {
    const { calendarDays, weekDayLabels } = useMonthGrid(viewDate);

    const showPreviousMonth = useCallback(
      () => setViewDate((previousViewDate) => subMonths(previousViewDate, 1)),
      [setViewDate]
    );

    const showNextMonth = useCallback(
      () => setViewDate((previousViewDate) => addMonths(previousViewDate, 1)),
      [setViewDate]
    );

    const isDayDisabled = useCallback(
      (day: Date) => {
        if (!isSameMonth(day, viewDate)) {
          return true;
        }

        const dayEnd = endOfDay(day).getTime();

        if (dayEnd < Date.now()) {
          return true;
        }

        if (minDate && dayEnd < minDate.getTime()) {
          return true;
        }

        return Boolean(maxDate && startOfDay(day).getTime() > maxDate.getTime());
      },
      [viewDate, minDate, maxDate]
    );

    return (
      <div className={classes['calendar-grid']}>
        <div className={classes['calendar-grid__header']}>
          <button
            type={'button'}
            className={classes['calendar-grid__nav']}
            onClick={showPreviousMonth}
          >
            <svg className={classes['calendar-grid__nav-icon']}>
              <use href={`${sprite}#left-arrow`} />
            </svg>
          </button>

          <span className={classes['calendar-grid__title']}>
            {format(viewDate, 'MMMM yyyy', { locale: getDateFnsLocale() })}
          </span>

          <button type={'button'} className={classes['calendar-grid__nav']} onClick={showNextMonth}>
            <svg className={classes['calendar-grid__nav-icon']}>
              <use href={`${sprite}#right-arrow`} />
            </svg>
          </button>
        </div>

        <div className={classes['calendar-grid__weekdays']}>
          {weekDayLabels.map((label) => (
            <span key={label} className={classes['calendar-grid__weekday']}>
              {label}
            </span>
          ))}
        </div>

        <div className={classes['calendar-grid__days']}>
          {calendarDays.map((day) => {
            const isSelected = Boolean(selectedDay && isSameDay(day, selectedDay));

            return (
              <button
                key={format(day, 'yyyy-MM-dd')}
                type={'button'}
                className={classNames(classes['calendar-grid__day'], {
                  [classes['calendar-grid__day--selected']]: isSelected,
                  [classes['calendar-grid__day--today']]: isToday(day),
                })}
                disabled={isDayDisabled(day)}
                onClick={() => onSelectDay(day)}
              >
                {format(day, 'd')}
              </button>
            );
          })}
        </div>
      </div>
    );
  }
);
